import { DateTime } from "luxon";

import {
  ICreatePartRequest,
  IPartRequestFilter,
  IPartRequestYardDetails,
  PartRequestContactPreference,
  PartRequestMatchStrength,
  PartRequestSource,
  PartRequestStatus,
} from "../models/partRequests/IPartRequest";

/** "2015 Ford Focus (AB12 CDE)" from whatever vehicle fields a request carries. */
export function partRequestVehicleLabel(request: {
  vrm?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
}): string {
  const label = [request.year ? String(request.year) : null, request.make, request.model]
    .filter(Boolean)
    .join(" ");
  if (request.vrm) return label ? `${label} (${request.vrm})` : request.vrm;
  return label || "Vehicle not specified";
}

export const PART_REQUEST_SOURCE_LABELS: Record<PartRequestSource, string> = {
  [PartRequestSource.Internal]: "Internal",
  [PartRequestSource.PartPro]: "PartPro",
};

export const PART_REQUEST_STATUS_LABELS: Record<PartRequestStatus, string> = {
  [PartRequestStatus.Open]: "Open",
  [PartRequestStatus.Claimed]: "Claimed",
  [PartRequestStatus.Fulfilled]: "Fulfilled",
  [PartRequestStatus.Closed]: "Closed",
};

export const PART_REQUEST_MATCH_LABELS: Record<PartRequestMatchStrength, string> = {
  [PartRequestMatchStrength.None]: "",
  [PartRequestMatchStrength.Possible]: "Possible",
  [PartRequestMatchStrength.Likely]: "Likely",
  [PartRequestMatchStrength.Exact]: "Exact",
};

export type PartRequestSourceFilter = "all" | "partpro" | "internal";
export type PartRequestStatusFilter = "live" | "open" | "claimed" | "fulfilled" | "closed" | "all";

/**
 * Turns the list page's toggles into the searchBody the API expects. "live" (the default)
 * is open + claimed, which is what the API returns when neither status nor includeClosed is set.
 */
export function buildPartRequestFilter(
  source: PartRequestSourceFilter,
  status: PartRequestStatusFilter,
  options: { onlyMine?: boolean; onlyMatched?: boolean } = {}
): IPartRequestFilter {
  const filter: IPartRequestFilter = {};

  if (source === "partpro") filter.source = PartRequestSource.PartPro;
  if (source === "internal") filter.source = PartRequestSource.Internal;

  switch (status) {
    case "open":
      filter.status = PartRequestStatus.Open;
      break;
    case "claimed":
      filter.status = PartRequestStatus.Claimed;
      break;
    case "fulfilled":
      filter.status = PartRequestStatus.Fulfilled;
      break;
    case "closed":
      filter.status = PartRequestStatus.Closed;
      break;
    case "all":
      filter.includeClosed = true;
      break;
    case "live":
    default:
      break;
  }

  if (options.onlyMine) filter.onlyMine = true;
  if (options.onlyMatched) filter.onlyMatched = true;

  return filter;
}

export interface PartRequestFormValues {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  contactPreference: PartRequestContactPreference;
  vrm: string;
  make: string;
  model: string;
  year: string;
  partWanted: string;
  details: string;
  partCategoryId?: string;
  engineCode?: string;
  stockSearchId?: string | null;
  partNumber: string;
  paintCode: string;
}

const clean = (value: string | undefined | null) => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

/** Trims the form, blanks become null, year becomes a number (or null when not a plausible year). */
export function toCreatePartRequest(values: PartRequestFormValues): ICreatePartRequest {
  const year = Number(values.year);
  return {
    customerName: (values.customerName ?? "").trim(),
    customerPhone: clean(values.customerPhone),
    customerEmail: clean(values.customerEmail),
    contactPreference: Number(values.contactPreference) as PartRequestContactPreference,
    vrm: clean(values.vrm)?.toUpperCase() ?? null,
    make: clean(values.make),
    model: clean(values.model),
    year: Number.isInteger(year) && year >= 1950 && year <= 2100 ? year : null,
    partWanted: (values.partWanted ?? "").trim(),
    details: clean(values.details),
    partCategoryId: clean(values.partCategoryId),
    engineCode: clean(values.engineCode)?.toUpperCase() ?? null,
    stockSearchId: values.stockSearchId ?? null,
    partNumber: clean(values.partNumber),
    paintCode: clean(values.paintCode),
  };
}

/** The yard's part number / paint code editor: trimmed, and a blank clears the field. */
export function toPartRequestYardDetails(values: { partNumber?: string | null; paintCode?: string | null }): IPartRequestYardDetails {
  return {
    partNumber: clean(values.partNumber),
    paintCode: clean(values.paintCode),
  };
}

/** Manufacture year from a DataOnly VRM lookup — model year first, then build / visibility dates. */
export function yearFromVehicleLookup(vehicle: {
  modelYear?: number | null;
  manufacturerBuildDate?: string | null;
  visibilityDate?: string | null;
}): string {
  if (
    vehicle.modelYear != null &&
    Number.isInteger(vehicle.modelYear) &&
    vehicle.modelYear >= 1950 &&
    vehicle.modelYear <= 2100
  ) {
    return String(vehicle.modelYear);
  }
  for (const raw of [vehicle.manufacturerBuildDate, vehicle.visibilityDate]) {
    if (!raw) continue;
    const year = DateTime.fromISO(raw).year;
    if (Number.isInteger(year) && year >= 1950 && year <= 2100) return String(year);
  }
  return "";
}

/** Client-side check mirroring the API's refusals so the form can say what is missing. */
export function validatePartRequestForm(values: PartRequestFormValues): string | null {
  const request = toCreatePartRequest(values);
  if (!request.partWanted) return "Say which part the customer wants";
  if (!request.customerName) return "Customer name is required";
  if (!request.vrm && !request.make) return "Give the vehicle - a registration or at least the make";
  if (request.contactPreference === PartRequestContactPreference.Phone && !request.customerPhone)
    return "A phone number is needed when the customer wants a call";
  if (request.contactPreference === PartRequestContactPreference.Email && !request.customerEmail)
    return "An email address is needed when the customer wants an email";
  return null;
}
